import { IPublicMenuTheme, LEGACY_PUBLIC_MENU_THEME, PublicMenuCardLayout } from "./PublicMenuTheme"

export interface ISystemPublicMenuTheme {
  id: string
  nameKey: string
  theme: IPublicMenuTheme
}

type ThemePalette = {
  primaryColor: string
  secondaryColor: string
  accentColor: string
  backgroundColor: string
  surfaceColor: string
  textColor: string
  buttonColor: string
  cardLayout?: PublicMenuCardLayout
  useGradientHeader?: boolean
}

const createPreset = (id: string, nameKey: string, palette: ThemePalette): ISystemPublicMenuTheme => {
  const theme: IPublicMenuTheme = {
    ...LEGACY_PUBLIC_MENU_THEME,
    ...palette,
    borderColor: palette.secondaryColor,
    searchBackgroundColor: palette.surfaceColor,
    searchBorderColor: palette.secondaryColor,
    headerBackgroundColor: palette.surfaceColor,
    storeNameColor: palette.textColor,
    storeDescriptionColor: palette.textColor,
    statusCardBackgroundColor: palette.secondaryColor,
    cartButtonColor: palette.primaryColor,
    cartBackgroundColor: palette.surfaceColor,
    cartBorderColor: palette.secondaryColor,
    cartTitleTextColor: palette.primaryColor,
    cartDescriptionTextColor: palette.textColor,
    cartTotalLabelTextColor: palette.textColor,
    cartTotalValueTextColor: palette.textColor,
    emptyStateTitleColor: palette.textColor,
    emptyStateDescriptionColor: palette.textColor,
    categoryBackgroundColor: palette.surfaceColor,
    categoryBorderColor: palette.secondaryColor,
    categoryDividerColor: palette.secondaryColor,
    loginButtonBackgroundColor: palette.surfaceColor,
    loginButtonTextColor: palette.textColor,
    shareButtonBackgroundColor: palette.secondaryColor,
    shareButtonTextColor: palette.primaryColor,
    serviceSelectorBackgroundColor: palette.secondaryColor,
    serviceSelectorTextColor: palette.textColor,
    serviceSelectorActiveBackgroundColor: palette.primaryColor,
    serviceSelectorActiveTextColor: palette.buttonColor === "#FFFFFF" ? palette.textColor : "#FFFFFF",
    mobileNavBackgroundColor: palette.surfaceColor,
    mobileNavActiveBackgroundColor: palette.secondaryColor,
    mobileNavActiveTextColor: palette.primaryColor,
    mobileNavActiveIconColor: palette.accentColor,
    mobileNavInactiveBackgroundColor: palette.surfaceColor,
    mobileNavInactiveTextColor: palette.textColor,
    mobileNavInactiveIconColor: palette.textColor,
  }

  return { id, nameKey, theme }
}

export const SYSTEM_PUBLIC_MENU_THEMES: ISystemPublicMenuTheme[] = [
  {
    id: "system-default",
    nameKey: "public_menu_customization.themes.preset_names.system_default",
    theme: LEGACY_PUBLIC_MENU_THEME,
  },
  createPreset("mothers-day", "public_menu_customization.themes.preset_names.mothers_day", {
    primaryColor: "#DB2777",
    secondaryColor: "#FCE7F3",
    accentColor: "#BE185D",
    backgroundColor: "#FFF7FB",
    surfaceColor: "#FFFFFF",
    textColor: "#4A1833",
    buttonColor: "#DB2777",
    useGradientHeader: true,
  }),
  createPreset("fathers-day", "public_menu_customization.themes.preset_names.fathers_day", {
    primaryColor: "#1D4ED8",
    secondaryColor: "#DBEAFE",
    accentColor: "#0F766E",
    backgroundColor: "#F8FAFC",
    surfaceColor: "#FFFFFF",
    textColor: "#172554",
    buttonColor: "#1D4ED8",
    cardLayout: "compact-left",
  }),
  createPreset("new-year", "public_menu_customization.themes.preset_names.new_year", {
    primaryColor: "#D4AF37",
    secondaryColor: "#27272A",
    accentColor: "#FDE68A",
    backgroundColor: "#09090B",
    surfaceColor: "#18181B",
    textColor: "#FAFAFA",
    buttonColor: "#D4AF37",
    useGradientHeader: true,
  }),
  createPreset("christmas", "public_menu_customization.themes.preset_names.christmas", {
    primaryColor: "#B91C1C",
    secondaryColor: "#DCFCE7",
    accentColor: "#15803D",
    backgroundColor: "#FFF7F7",
    surfaceColor: "#FFFFFF",
    textColor: "#3F1212",
    buttonColor: "#15803D",
    useGradientHeader: true,
  }),
  createPreset("valentines", "public_menu_customization.themes.preset_names.valentines", {
    primaryColor: "#E11D48",
    secondaryColor: "#FFE4E6",
    accentColor: "#9F1239",
    backgroundColor: "#FFF1F2",
    surfaceColor: "#FFFFFF",
    textColor: "#4C0519",
    buttonColor: "#E11D48",
  }),
  createPreset("easter", "public_menu_customization.themes.preset_names.easter", {
    primaryColor: "#7C3AED",
    secondaryColor: "#EDE9FE",
    accentColor: "#F59E0B",
    backgroundColor: "#FAF5FF",
    surfaceColor: "#FFFFFF",
    textColor: "#3B0764",
    buttonColor: "#7C3AED",
    cardLayout: "compact-right",
  }),
  createPreset("spring", "public_menu_customization.themes.preset_names.spring", {
    primaryColor: "#16A34A",
    secondaryColor: "#DCFCE7",
    accentColor: "#F472B6",
    backgroundColor: "#F0FDF4",
    surfaceColor: "#FFFFFF",
    textColor: "#14532D",
    buttonColor: "#16A34A",
    useGradientHeader: true,
  }),
  createPreset("summer", "public_menu_customization.themes.preset_names.summer", {
    primaryColor: "#F97316",
    secondaryColor: "#FFEDD5",
    accentColor: "#06B6D4",
    backgroundColor: "#FFF7ED",
    surfaceColor: "#FFFFFF",
    textColor: "#431407",
    buttonColor: "#06B6D4",
  }),
  createPreset("autumn", "public_menu_customization.themes.preset_names.autumn", {
    primaryColor: "#B45309",
    secondaryColor: "#FEF3C7",
    accentColor: "#92400E",
    backgroundColor: "#FFFBEB",
    surfaceColor: "#FFFFFF",
    textColor: "#451A03",
    buttonColor: "#B45309",
    cardLayout: "compact-left",
  }),
  createPreset("winter", "public_menu_customization.themes.preset_names.winter", {
    primaryColor: "#0369A1",
    secondaryColor: "#E0F2FE",
    accentColor: "#64748B",
    backgroundColor: "#F0F9FF",
    surfaceColor: "#FFFFFF",
    textColor: "#0C4A6E",
    buttonColor: "#0369A1",
  }),
  createPreset("night", "public_menu_customization.themes.preset_names.night", {
    primaryColor: "#A78BFA",
    secondaryColor: "#312E81",
    accentColor: "#FBBF24",
    backgroundColor: "#111827",
    surfaceColor: "#1F2937",
    textColor: "#F9FAFB",
    buttonColor: "#7C3AED",
    useGradientHeader: true,
  }),
  createPreset("premium", "public_menu_customization.themes.preset_names.premium", {
    primaryColor: "#92400E",
    secondaryColor: "#FEF3C7",
    accentColor: "#B45309",
    backgroundColor: "#FFFBEB",
    surfaceColor: "#FFFFFF",
    textColor: "#451A03",
    buttonColor: "#92400E",
    cardLayout: "compact-right",
  }),
  createPreset("urban", "public_menu_customization.themes.preset_names.urban", {
    primaryColor: "#F59E0B",
    secondaryColor: "#27272A",
    accentColor: "#EF4444",
    backgroundColor: "#18181B",
    surfaceColor: "#27272A",
    textColor: "#FAFAFA",
    buttonColor: "#EF4444",
    useGradientHeader: true,
  }),
  createPreset("fresh", "public_menu_customization.themes.preset_names.fresh", {
    primaryColor: "#DC2626",
    secondaryColor: "#FEE2E2",
    accentColor: "#16A34A",
    backgroundColor: "#FFF7ED",
    surfaceColor: "#FFFFFF",
    textColor: "#450A0A",
    buttonColor: "#16A34A",
  }),
  createPreset("boutique", "public_menu_customization.themes.preset_names.boutique", {
    primaryColor: "#BE123C",
    secondaryColor: "#1F2937",
    accentColor: "#F9A8D4",
    backgroundColor: "#111827",
    surfaceColor: "#1F2937",
    textColor: "#F9FAFB",
    buttonColor: "#BE123C",
    cardLayout: "compact-left",
  }),
  createPreset("natural", "public_menu_customization.themes.preset_names.natural", {
    primaryColor: "#65A30D",
    secondaryColor: "#ECFCCB",
    accentColor: "#16A34A",
    backgroundColor: "#F7FEE7",
    surfaceColor: "#FFFFFF",
    textColor: "#365314",
    buttonColor: "#65A30D",
  }),
  createPreset("ocean", "public_menu_customization.themes.preset_names.ocean", {
    primaryColor: "#0284C7",
    secondaryColor: "#E0F2FE",
    accentColor: "#0891B2",
    backgroundColor: "#F0F9FF",
    surfaceColor: "#FFFFFF",
    textColor: "#0C4A6E",
    buttonColor: "#0284C7",
    useGradientHeader: true,
  }),
  createPreset("lavender", "public_menu_customization.themes.preset_names.lavender", {
    primaryColor: "#8B5CF6",
    secondaryColor: "#EDE9FE",
    accentColor: "#C084FC",
    backgroundColor: "#FAF5FF",
    surfaceColor: "#FFFFFF",
    textColor: "#4C1D95",
    buttonColor: "#8B5CF6",
  }),
  createPreset("minimal", "public_menu_customization.themes.preset_names.minimal", {
    primaryColor: "#18181B",
    secondaryColor: "#F4F4F5",
    accentColor: "#71717A",
    backgroundColor: "#FFFFFF",
    surfaceColor: "#FFFFFF",
    textColor: "#18181B",
    buttonColor: "#18181B",
    cardLayout: "compact-left",
  }),
  createPreset("rose-gold", "public_menu_customization.themes.preset_names.rose_gold", {
    primaryColor: "#BE7C68",
    secondaryColor: "#FCE7E1",
    accentColor: "#9D5C4A",
    backgroundColor: "#FFF8F6",
    surfaceColor: "#FFFFFF",
    textColor: "#5C2E24",
    buttonColor: "#BE7C68",
    useGradientHeader: true,
  }),
  createPreset("neon", "public_menu_customization.themes.preset_names.neon", {
    primaryColor: "#22D3EE",
    secondaryColor: "#3F3F46",
    accentColor: "#A3E635",
    backgroundColor: "#09090B",
    surfaceColor: "#18181B",
    textColor: "#FAFAFA",
    buttonColor: "#22D3EE",
    useGradientHeader: true,
  }),
  createPreset("corporate", "public_menu_customization.themes.preset_names.corporate", {
    primaryColor: "#334155",
    secondaryColor: "#E2E8F0",
    accentColor: "#0F766E",
    backgroundColor: "#F8FAFC",
    surfaceColor: "#FFFFFF",
    textColor: "#0F172A",
    buttonColor: "#0F766E",
    cardLayout: "compact-right",
  }),
  createPreset("candy", "public_menu_customization.themes.preset_names.candy", {
    primaryColor: "#EC4899",
    secondaryColor: "#FCE7F3",
    accentColor: "#8B5CF6",
    backgroundColor: "#FDF4FF",
    surfaceColor: "#FFFFFF",
    textColor: "#581C87",
    buttonColor: "#EC4899",
    useGradientHeader: true,
  }),
  createPreset("forest", "public_menu_customization.themes.preset_names.forest", {
    primaryColor: "#166534",
    secondaryColor: "#DCFCE7",
    accentColor: "#854D0E",
    backgroundColor: "#F0FDF4",
    surfaceColor: "#FFFFFF",
    textColor: "#14532D",
    buttonColor: "#166534",
  }),
  createPreset("sunset", "public_menu_customization.themes.preset_names.sunset", {
    primaryColor: "#EA580C",
    secondaryColor: "#FFEDD5",
    accentColor: "#DB2777",
    backgroundColor: "#FFF7ED",
    surfaceColor: "#FFFFFF",
    textColor: "#7C2D12",
    buttonColor: "#DB2777",
    useGradientHeader: true,
  }),
  createPreset("black-gold", "public_menu_customization.themes.preset_names.black_gold", {
    primaryColor: "#EAB308",
    secondaryColor: "#27272A",
    accentColor: "#FDE047",
    backgroundColor: "#09090B",
    surfaceColor: "#18181B",
    textColor: "#FAFAFA",
    buttonColor: "#EAB308",
    cardLayout: "compact-right",
  }),
  createPreset("electronics", "public_menu_customization.themes.preset_names.electronics", {
    primaryColor: "#2563EB",
    secondaryColor: "#DBEAFE",
    accentColor: "#06B6D4",
    backgroundColor: "#F8FAFC",
    surfaceColor: "#FFFFFF",
    textColor: "#172554",
    buttonColor: "#2563EB",
    useGradientHeader: true,
  }),
  createPreset("fashion", "public_menu_customization.themes.preset_names.fashion", {
    primaryColor: "#C026D3",
    secondaryColor: "#FAE8FF",
    accentColor: "#F472B6",
    backgroundColor: "#FDF4FF",
    surfaceColor: "#FFFFFF",
    textColor: "#701A75",
    buttonColor: "#C026D3",
    cardLayout: "compact-right",
  }),
  createPreset("home-decor", "public_menu_customization.themes.preset_names.home_decor", {
    primaryColor: "#854D0E",
    secondaryColor: "#FEF3C7",
    accentColor: "#166534",
    backgroundColor: "#FFFBEB",
    surfaceColor: "#FFFFFF",
    textColor: "#451A03",
    buttonColor: "#854D0E",
    cardLayout: "compact-left",
  }),
  createPreset("beauty", "public_menu_customization.themes.preset_names.beauty", {
    primaryColor: "#DB2777",
    secondaryColor: "#FCE7F3",
    accentColor: "#8B5CF6",
    backgroundColor: "#FDF4FF",
    surfaceColor: "#FFFFFF",
    textColor: "#581C87",
    buttonColor: "#DB2777",
    useGradientHeader: true,
  }),
  createPreset("semi-jewelry", "public_menu_customization.themes.preset_names.semi_jewelry", {
    primaryColor: "#8A5A2B",
    secondaryColor: "#F3E5C8",
    accentColor: "#C9A227",
    backgroundColor: "#FFFCF5",
    surfaceColor: "#FFFFFF",
    textColor: "#3D2B1F",
    buttonColor: "#DEAA3A",
    cardLayout: "compact-right",
    useGradientHeader: true,
  }),
  createPreset("kids", "public_menu_customization.themes.preset_names.kids", {
    primaryColor: "#0EA5E9",
    secondaryColor: "#E0F2FE",
    accentColor: "#F59E0B",
    backgroundColor: "#F0F9FF",
    surfaceColor: "#FFFFFF",
    textColor: "#0C4A6E",
    buttonColor: "#0EA5E9",
  }),
]
